import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import Reveal, { SectionHeading, EASE } from "../components/Reveal";
import ProductZoom from "../components/ProductZoom";
import { useEnquiry } from "../enquiry-context";
import { PRODUCTS } from "../data";

function EnquiryButton({ product, testid }) {
  const { openEnquiry } = useEnquiry();
  return (
    <button
      data-testid={testid}
      onClick={() => openEnquiry(product)}
      className="group/btn inline-flex items-center gap-2 rounded-full border border-amber-600/60 px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-amber-800 hover:bg-amber-500 hover:border-amber-500 hover:text-stone-900 transition-all"
    >
      Send Enquiry
      <ArrowUpRight size={14} strokeWidth={2.5} className="transition-transform group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
    </button>
  );
}

function ProductMedia({ src, alt, testid, onZoom }) {
  return (
    <button
      type="button"
      onClick={onZoom}
      data-testid={testid}
      aria-label={`View ${alt} in full screen`}
      className="group/zoom relative block w-full cursor-zoom-in overflow-hidden bg-[#0D1530]"
    >
      <motion.img
        src={src}
        alt={alt}
        loading="lazy"
        className="w-full aspect-[4/3] object-cover"
        whileHover={{ scale: 1.05 }}
        transition={{ duration: 0.7, ease: EASE }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#080C14]/55 via-transparent to-transparent pointer-events-none" />
      <span className="absolute bottom-3 right-3 z-10 rounded-full bg-black/60 px-3 py-1.5 text-[10px] font-tech uppercase tracking-widest text-white/90 opacity-0 backdrop-blur-sm transition-opacity group-hover/zoom:opacity-100">
        Zoom +
      </span>
    </button>
  );
}

export default function Products() {
  const [zoom, setZoom] = useState(null);

  const SERIES_ORDER = ["Filter Assemblies", "Candle Stands", "Decorative & Art Castings"];
  const SERIES_MAP = {
    "cav-single": "Filter Assemblies",
    "water-separator": "Filter Assemblies",
    "single-big": "Filter Assemblies",
    "cav-double": "Filter Assemblies",
    "diesel-1ltr": "Filter Assemblies",
    "diesel-half-ltr": "Filter Assemblies",
    "candle-stand": "Candle Stands",
    "single-arm-candle-stand": "Candle Stands",
    "three-arm-candle-stand": "Candle Stands",
    "cricket-statue": "Decorative & Art Castings",
    "aluminium-base": "Decorative & Art Castings",
    snowflake: "Decorative & Art Castings",
  };

  return (
    <section id="products" className="relative py-24 lg:py-32 bg-[#F3EFE6] border-y border-stone-200">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHeading eyebrow="Products We Manufacture" title={<>Cast components, ready for your line</>} />
          <Reveal delay={0.2}>
            <p className="max-w-sm text-sm leading-relaxed text-stone-600">
              We also develop customised components according to customer drawings, samples and
              specifications.
            </p>
          </Reveal>
        </div>

        <div className="mt-14 space-y-16" data-testid="products-series">
          {SERIES_ORDER.map((series, si) => {
            const items = PRODUCTS.filter((p) => SERIES_MAP[p.id] === series);
            return (
              <div key={series} data-testid={`products-series-${si}`}>
                <Reveal>
                  <div className="mb-7 flex items-center gap-4">
                    <span className="font-tech text-xs font-bold text-amber-700">
                      {String(si + 1).padStart(2, "0")}
                    </span>
                    <h3 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight text-stone-900">
                      {series}
                    </h3>
                    <span className="h-px flex-1 bg-stone-300" />
                    <span className="font-tech text-[11px] uppercase tracking-widest text-stone-500">
                      {items.length} {items.length === 1 ? "product" : "products"}
                    </span>
                  </div>
                </Reveal>
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {items.map((p, i) =>
                    p.imageOnly ? (
                      <Reveal key={p.id} delay={0.04 * i} className="self-start">
                        <button
                          type="button"
                          onClick={() => setZoom({ src: p.img, name: "" })}
                          data-testid={`product-image-zoom-${p.id}`}
                          aria-label="View product image in full screen"
                          className="group/zoom relative block w-full cursor-zoom-in overflow-hidden rounded-xl border border-stone-200 bg-white shadow-[0_8px_30px_rgba(20,23,28,0.05)] hover:border-amber-500/60 hover:shadow-[0_24px_60px_rgba(150,112,31,0.16)] transition-all"
                        >
                          <div className="relative overflow-hidden bg-[#0D1530]">
                            <motion.img
                              src={p.img}
                              alt="Die cast product"
                              loading="lazy"
                              className="w-full aspect-[4/3] object-cover"
                              whileHover={{ scale: 1.05 }}
                              transition={{ duration: 0.7, ease: EASE }}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#080C14]/55 via-transparent to-transparent pointer-events-none" />
                            <span className="absolute bottom-3 right-3 z-10 rounded-full bg-black/60 px-3 py-1.5 text-[10px] font-tech uppercase tracking-widest text-white/90 opacity-0 backdrop-blur-sm transition-opacity group-hover/zoom:opacity-100">
                              Zoom +
                            </span>
                          </div>
                        </button>
                      </Reveal>
                    ) : (
                      <Reveal key={p.id} delay={0.04 * i} className="h-full">
                        <div
                          data-testid={`product-card-${p.id}`}
                          className="group flex h-full flex-col overflow-hidden rounded-xl border border-stone-200 bg-white shadow-[0_8px_30px_rgba(20,23,28,0.05)] hover:border-amber-500/60 hover:shadow-[0_24px_60px_rgba(150,112,31,0.16)] transition-all"
                        >
                          <ProductMedia
                            src={p.img}
                            alt={p.name}
                            testid={`product-image-zoom-${p.id}`}
                            onZoom={() => setZoom({ src: p.img, name: p.name })}
                          />
                          <div className="flex flex-1 flex-col p-5">
                            <span className="w-max rounded-full border border-stone-300 px-2.5 py-0.5 text-[10px] font-tech uppercase tracking-widest text-stone-500 group-hover:text-amber-800 group-hover:border-amber-500/60 transition-colors">
                              {p.tag}
                            </span>
                            <h3 className="mt-3 font-display text-xl font-bold uppercase leading-tight text-stone-900">
                              {p.name}
                            </h3>
                            <p className="mt-2 flex-1 text-[13px] leading-relaxed text-stone-600">{p.desc}</p>
                            <div className="mt-4">
                              <EnquiryButton product={p.name} testid={`btn-enquiry-${p.id}`} />
                            </div>
                          </div>
                        </div>
                      </Reveal>
                    )
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-12">
          <Reveal delay={0.1}>
            <div
              data-testid="product-card-custom-cta"
              className="relative flex flex-col justify-center overflow-hidden rounded-xl border border-amber-500/30 bg-[#0D1530] p-6 lg:flex-row lg:items-center lg:justify-between lg:gap-8 lg:p-8"
            >
              <div className="glow-pulse pointer-events-none absolute -top-16 -right-16 h-48 w-48 rounded-full bg-amber-500/20 blur-[70px]" />
              <div>
                <p className="font-tech text-[10px] uppercase tracking-[0.25em] text-amber-400">
                  Custom Development
                </p>
                <h3 className="mt-3 font-display text-2xl font-bold uppercase leading-tight text-stone-50">
                  Send your drawing. Get your component.
                </h3>
                <p className="mt-2 max-w-xl text-[13px] leading-relaxed text-stone-400">
                  Customised components developed according to your drawings, samples and
                  specifications.
                </p>
              </div>
              <div className="mt-5 shrink-0 lg:mt-0">
                <EnquiryButton
                  product="Custom Component (Drawing / Sample)"
                  testid="btn-enquiry-custom-component"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </div>
      <ProductZoom zoom={zoom} onClose={() => setZoom(null)} />
    </section>
  );
}
